
import { useState } from 'react';
import Button from '../../../components/base/Button';

interface Product {
  id: number;
  title: string;
  category: string;
  brand: string;
  model: string;
  certification: string;
  status: string;
  image: string;
  description: string;
}

interface ProductDetailHeroProps {
  product: Product;
}

export default function ProductDetailHero({ product }: ProductDetailHeroProps) {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-8">
          <a href="/" className="hover:text-red-600 transition-colors">Home</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href="/products" className="hover:text-red-600 transition-colors">Products</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-900">{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden">
              <img
                src={product.image}
                alt={product.title}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Share Buttons */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Share:</span>
              <div className="flex space-x-2">
                <button className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                  <i className="ri-facebook-fill text-sm"></i>
                </button>
                <button className="w-8 h-8 bg-blue-400 text-white rounded-full flex items-center justify-center hover:bg-blue-500 transition-colors">
                  <i className="ri-twitter-fill text-sm"></i>
                </button>
                <button className="w-8 h-8 bg-blue-800 text-white rounded-full flex items-center justify-center hover:bg-blue-900 transition-colors">
                  <i className="ri-linkedin-fill text-sm"></i>
                </button>
                <button className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center hover:bg-green-700 transition-colors">
                  <i className="ri-whatsapp-fill text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <span className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
                  {product.category}
                </span>
                <span className="text-sm text-gray-500">Model: {product.model}</span>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.title}</h1>
              <p className="text-lg text-gray-600 leading-relaxed">{product.description}</p>
            </div>

            {/* Product Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Brand</div>
                <div className="font-semibold text-gray-900">{product.brand}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Certification</div>
                <div className="font-semibold text-gray-900">{product.certification}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Status</div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="font-semibold text-gray-900">{product.status}</span>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Model</div>
                <div className="font-semibold text-gray-900">{product.model}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <div className="flex space-x-4">
                <Button variant="primary" className="flex-1 whitespace-nowrap cursor-pointer">
                  <i className="ri-message-3-line mr-2"></i>
                  Request Quote
                </Button>
                <Button variant="outline" className="flex-1 whitespace-nowrap cursor-pointer">
                  <i className="ri-download-line mr-2"></i>
                  Download Datasheet
                </Button>
              </div>
              
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                <i className="ri-phone-line"></i>
                <span>Need help? Call us at</span>
                <a href="tel:+97455544093" className="text-red-600 font-semibold hover:underline">
                  +974 5554 4093
                </a>
              </div>
            </div>

            {/* Quick Info Tabs */}
            <div className="border-t pt-6">
              <div className="flex space-x-6 border-b">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'overview'
                      ? 'border-red-600 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('applications')}
                  className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'applications'
                      ? 'border-red-600 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Applications
                </button>
              </div>
              
              <div className="pt-4">
                {activeTab === 'overview' && (
                  <div className="text-sm text-gray-600 space-y-2">
                    <p>FM and UL approved control valves for reliable flow control in fire protection systems.</p>
                    <p>Available in butterfly, gate, and specialty valve configurations.</p>
                  </div>
                )}
                {activeTab === 'applications' && (
                  <div className="text-sm text-gray-600 space-y-2">
                    <p>• Sprinkler system control</p>
                    <p>• Hydrant network management</p>
                    <p>• Fire pump discharge control</p>
                    <p>• Water supply isolation</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
